import React from 'react';
import { Info, ListChecks, RefreshCw } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
import { VerdictBadge } from '../ui/VerdictBadge';
const CODEX_URL = 'https://www.fao.org/ag/humannutrition/32444-09f5545b8abe9a0c3baf01a4502ac36e4.pdf';
const AO_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/03/Administrative-Order-No.-2014-0030.pdf';
type Claim = {
  text: string;
  classification: string;
  verdict: 'Permitted' | 'Permitted with conditions' | 'Not permitted';
  reference?: string;
  recommendation: string;
  flag?: string;
  citation?: {
    text: string;
    url: string;
  };
};
const claims: Claim[] = [{
  text: '27% MORE PROTEIN',
  classification: 'Nutrition',
  verdict: 'Not permitted',
  reference: 'Codex CAC/GL 23-1997 §6.3',
  recommendation: 'Absolute difference between the product (26.9g/100g) and the reference (~21.3g/100g) is under 6g/100g, short of the required 7.1g/100g minimum (10% of the 71g PH protein reference) — even though the 27% relative figure clears the 25% relative-difference threshold. Both tests must pass. Remove the comparative claim, or substantiate a larger absolute protein difference.',
  citation: {
    text: 'Codex CAC/GL 23-1997 §6.3',
    url: CODEX_URL
  }
}, {
  text: 'HALAL',
  classification: 'Certification',
  verdict: 'Permitted with conditions',
  recommendation: "Generic halal seal graphic with no accredited certifying-body name (e.g. HDIP, HICCIP, IDCP), logo, or certificate number. Add a recognized halal certifying body's logo and certificate number, or remove the claim if not certified.",
  flag: 'Needs: substantiating evidence'
}, {
  text: '57% LESS FAT',
  classification: 'Nutrition',
  verdict: 'Permitted with conditions',
  reference: 'Codex CAC/GL 23-1997 §6.2',
  recommendation: "The claim already carries a linking asterisk to its back-panel footnote — no missing symbol. Open question: the reference-food identity should appear 'in close proximity' to the claim, and the footnote sits on a different panel. Confirm with regulatory counsel whether current placement satisfies 'close proximity,' or relocate/duplicate the footnote nearer the claim.",
  citation: {
    text: 'Codex CAC/GL 23-1997 §6.2',
    url: CODEX_URL
  }
}, {
  text: 'SOURCE OF CALCIUM',
  classification: 'Nutrition',
  verdict: 'Permitted',
  reference: 'Codex CAC/GL 23-1997',
  recommendation: "Clears the 15% NRV 'source' threshold several times over (659mg/100g vs. a ~150mg/100g floor).",
  citation: {
    text: 'Codex CAC/GL 23-1997',
    url: CODEX_URL
  }
}, {
  text: '8g PROTEIN PER SERVE',
  classification: 'Nutrition',
  verdict: 'Permitted',
  reference: 'Codex CAC/GL 23-1997',
  recommendation: "26.9g/100g clears even the Codex 'high' protein threshold (20% NRV/100g = 14.2g/100g) with room to spare.",
  citation: {
    text: 'Codex CAC/GL 23-1997',
    url: CODEX_URL
  }
}, {
  text: 'MADE IN NEW ZEALAND',
  classification: 'Origin',
  verdict: 'Permitted',
  reference: 'AO 2014-0030 §VI.A.5.e',
  recommendation: 'Consistent with the back-panel statement confirming New Zealand packing and ingredient sourcing.',
  citation: {
    text: 'AO 2014-0030 §VI.A.5.e',
    url: AO_URL
  }
}, {
  text: 'VEGETARIAN FRIENDLY',
  classification: 'Dietary suitability',
  verdict: 'Permitted',
  reference: 'RA 3720 §11',
  recommendation: 'No meat ingredients; rennet is non-animal. Note the citation is the general misbranding backstop rather than a vegetarian-claim-specific standard — fine as filed, worth a claim-specific citation if one exists on file.',
  citation: {
    text: 'RA 3720 §11',
    url: 'https://elibrary.judiciary.gov.ph/thebookshelf/showdocs/2/24284'
  }
}, {
  text: 'Packed with protein to help keep you going for longer',
  classification: 'Marketing',
  verdict: 'Permitted',
  reference: 'FDA Draft Circular — Acceptable Nutrition/Health Claims, Annex A',
  recommendation: 'General, non-quantified marketing phrasing, not a defined regulated claim type — not misleading given the genuinely high protein content.',
  citation: {
    text: 'FDA Draft Circular, Annex A',
    url: 'https://www.fda.gov.ph/wp-content/uploads/2024/07/Draft-FDA-Circular_-Guidelines-on-the-Use-of-Acceptable-Nutrition-and-Health-Claims.pdf'
  }
}, {
  text: 'NATURAL CHEESE',
  classification: 'Marketing',
  verdict: 'Permitted',
  reference: 'AO 2014-0030 §V.A & VII',
  recommendation: 'Consistent with a genuinely natural, unflavoured 79% cheese ingredient.',
  citation: {
    text: 'AO 2014-0030 §V.A & VII',
    url: AO_URL
  }
}];
export function PhClaimsTab() {
  return <div className="space-y-4" data-id="element-1538" data-mp="160o6gtao49m8">
      <header className="flex items-start gap-3" data-id="element-1539" data-mp="161g5ke3h4ph9">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-1540" data-mp="110k0hx7dyxa9">
          <ListChecks className="h-5 w-5" data-id="element-1541" data-mp="x09jlpi22mgm" />
        </span>
        <div data-id="element-1542" data-mp="x1kppvyz46aw">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-1543" data-mp="38mttob6rc572">Claims</h2>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#6b7d70]" data-id="element-1544" data-mp="3co5bhuje3m4p">
            Claims assessed against PH FDA labelling rules and the Codex comparative-claims framework.
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1545" data-mp="3mml3s0u5l1fs">
        <p className="text-sm text-[#718174]" data-id="element-1546" data-mp="2oz2w6tc8a5dd">{claims.length} claims assessed · reviewed 17 Aug 2026</p>
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-1547" data-mp="2tww1zl9mcxd4">
          <RefreshCw className="h-4 w-4" data-id="element-1548" data-mp="33pa6gls30i2z" />
          Re-analyse
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-1549" data-mp="265yz0xzsxae0">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-1550" data-mp="235ynnj3kwpd6" />
        <span data-id="element-1551" data-mp="2343zlvx0gh4v">
          Two claims need attention: &apos;27% MORE PROTEIN&apos; fails the absolute-difference test despite clearing the relative-percentage bar, and
          HALAL has no certifying body shown. One claim — &apos;57% LESS FAT&apos; — has an open proximity question worth a quick legal check.
        </span>
      </div>

      {claims.map(claim => <article key={claim.text} className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-1552" data-mp="1k84d30msy5so">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-1553" data-mp="1g7twku1dvwzk">
            <h3 className="font-[Figtree] text-lg font-semibold leading-snug text-evergreen" data-id="element-1554" data-mp="1u73f40d2b36p">{claim.text}</h3>
            <div className="flex shrink-0 items-center gap-2" data-id="element-1555" data-mp="1p8s43mb16mmw">
              <span className="rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-1556" data-mp="35jw5p7u49ll6">
                {claim.classification}
              </span>
              <VerdictBadge verdict={claim.verdict} data-id="element-1557" data-mp="mdblsmevc9tj" />
            </div>
          </div>
          <div className="mt-5" data-id="element-1558" data-mp="re90gp0o6lqa">
            {claim.reference && <>
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-1559" data-mp="de6m3crkmafs">Regulatory reference</p>
                <p className="mt-1 text-sm leading-relaxed text-[#56685c]" data-id="element-1560" data-mp="id1d31zoz5az">{claim.reference}</p>
              </>}
            <div className={claim.reference ? 'mt-4' : ''} data-id="element-1561" data-mp="164gacgcm2wfd">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-1562" data-mp="1a2e0pmi4bokc">Recommendation</p>
              <p className="mt-1 text-sm leading-relaxed text-[#506355]" data-id="element-1563" data-mp="wa6hyyno5y4n">{claim.recommendation}</p>
            </div>
            {claim.flag && <p className="mt-3 inline-flex rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900" data-id="element-1564" data-mp="1p0eua9vixobb">
                {claim.flag}
              </p>}
            {claim.citation && <div className="mt-4" data-id="element-1565" data-mp="3pwe2s6v7aaqv">
                <RegulationLink text={claim.citation.text} href={claim.citation.url} data-id="element-1566" data-mp="3l431azktagw7" />
              </div>}
          </div>
        </article>)}

      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-1567" data-mp="3g5jfr0a4r74d">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-1568" data-mp="co140hr8lfyo" />
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </div>
    </div>;
}